import React, { useEffect, useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import WaitlistForm from '../components/WaitlistForm';
import WaitlistCounter from '../components/WaitlistCounter';
import { 
  GraduationCap, 
  Lightbulb, 
  Users, 
  Shield, 
  BookOpen,
  Target,
  Sparkles,
  Phone,
  Mail,
  MapPin,
  MessageCircle,
  ChevronRight,
  Award,
  Clock,
  Heart
} from 'lucide-react';

const Home = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [counterRefresh, setCounterRefresh] = useState(0);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const handleNewSignup = () => {
    setCounterRefresh(prev => prev + 1);
  };

  const scrollToWaitlist = () => {
    document.getElementById('waitlist-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleWhatsApp = () => {
    window.open('https://wa.me/919227991023', '_blank');
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Fixed WhatsApp Button */}
      <button
        onClick={handleWhatsApp}
        className="fixed bottom-6 right-6 bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-2xl z-50 transition-all duration-300 hover:scale-110"
        aria-label="Contact on WhatsApp"
      >
        <MessageCircle className="w-6 h-6" />
      </button>

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-md border-b border-gray-200 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img 
                src="/images/sigma-logo-1.png"
                alt="Sigma International School Logo"
                className="h-20 w-auto"
              />
              <div className="hidden sm:block">
                <div className="text-xl font-bold text-blue-900 leading-tight">Sigma International School</div>
                <div className="text-xs text-gray-600">Premier Residential Learning</div>
              </div>
            </div>
            <Button 
              onClick={scrollToWaitlist}
              className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-6 shadow-md transition-all duration-300 hover:shadow-lg"
            >
              Join Waitlist
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-orange-50 opacity-60"></div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className={`transform transition-all duration-1000 ${isVisible ? 'translate-x-0 opacity-100' : '-translate-x-12 opacity-0'}`}>
              <div className="inline-block bg-orange-100 text-orange-600 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                Launching Academic Year 2027-28
              </div>
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Where Children Become <span className="text-blue-900">Future-Ready</span> Leaders
              </h1>
              <div className="mb-6">
                <WaitlistCounter variant="badge" refreshKey={counterRefresh} />
              </div>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Sigma International School offers more than education—we provide a transformative residential learning experience where your child builds confidence, independence, and life skills that last forever.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  onClick={scrollToWaitlist}
                  size="lg"
                  className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold px-8 py-6 text-lg shadow-xl transition-all duration-300 hover:shadow-2xl hover:scale-105"
                >
                  Join Founding Families Waitlist
                  <ChevronRight className="ml-2 w-5 h-5" />
                </Button>
                <Button 
                  onClick={handleWhatsApp}
                  size="lg"
                  variant="outline"
                  className="border-2 border-blue-900 text-blue-900 hover:bg-blue-900 hover:text-white font-semibold px-8 py-6 text-lg transition-all duration-300"
                >
                  <MessageCircle className="mr-2 w-5 h-5" />
                  Connect on WhatsApp
                </Button>
              </div>
            </div>
            <div className={`transform transition-all duration-1000 delay-300 ${isVisible ? 'translate-x-0 opacity-100' : 'translate-x-12 opacity-0'}`}>
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_future-ready-school-2/artifacts/yv7bmt62_WhatsApp%20Image%202026-06-04%20at%203.39.52%20PM.jpeg"
                  alt="Sigma International School Campus"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/20 to-transparent"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Emotional Hook Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-900 to-blue-800 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <Heart className="w-16 h-16 mx-auto mb-6 text-orange-400" />
          <h2 className="text-4xl font-bold mb-6">Every Parent Wants More Than Just Good Grades</h2>
          <p className="text-xl leading-relaxed opacity-90">
            You want your child to grow into a confident individual who can think independently, communicate effectively, and navigate life with resilience. You seek an environment that nurtures not just academic excellence, but character, discipline, and real-world skills.
          </p>
          <p className="text-xl leading-relaxed opacity-90 mt-6">
            That's exactly what Sigma International School is built for.
          </p>
        </div>
      </section>

      {/* What Makes Sigma Different - 4 Pillars */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">What Makes Sigma Different</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">Four pillars that create future-ready leaders</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="group hover:shadow-2xl transition-all duration-300 border-none bg-white hover:scale-105">
              <CardContent className="p-8">
                <div className="bg-blue-100 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-blue-900 transition-colors duration-300">
                  <GraduationCap className="w-8 h-8 text-blue-900 group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Academic Excellence</h3>
                <p className="text-gray-600 leading-relaxed">
                  Rigorous CBSE curriculum delivered by experienced educators, ensuring your child excels in academics while developing critical thinking and problem-solving abilities.
                </p>
              </CardContent>
            </Card>

            <Card className="group hover:shadow-2xl transition-all duration-300 border-none bg-white hover:scale-105">
              <CardContent className="p-8">
                <div className="bg-orange-100 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-orange-500 transition-colors duration-300">
                  <Lightbulb className="w-8 h-8 text-orange-500 group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Future-Ready Learning</h3>
                <p className="text-gray-600 leading-relaxed">
                  Beyond textbooks—we focus on communication skills, leadership development, creative thinking, and real-world problem-solving that prepares students for tomorrow's challenges.
                </p>
              </CardContent>
            </Card>

            <Card className="group hover:shadow-2xl transition-all duration-300 border-none bg-white hover:scale-105">
              <CardContent className="p-8">
                <div className="bg-green-100 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-green-600 transition-colors duration-300">
                  <Users className="w-8 h-8 text-green-600 group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Holistic Development</h3>
                <p className="text-gray-600 leading-relaxed">
                  A rich ecosystem of sports, arts, music, and extracurricular activities ensures balanced growth—nurturing both mind and body for well-rounded development.
                </p>
              </CardContent>
            </Card>

            <Card className="group hover:shadow-2xl transition-all duration-300 border-none bg-white hover:scale-105">
              <CardContent className="p-8">
                <div className="bg-purple-100 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-purple-600 transition-colors duration-300">
                  <Shield className="w-8 h-8 text-purple-600 group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Structured Residential Life</h3>
                <p className="text-gray-600 leading-relaxed">
                  A safe, disciplined, and nurturing residential environment where children learn independence, time management, and social skills in a supportive community.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Residential Schooling */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_future-ready-school-2/artifacts/d99bg4pq_WhatsApp%20Image%202026-06-04%20at%203.39.52%20PM%20%281%29.jpeg"
                alt="Residential School Campus"
                className="rounded-2xl shadow-2xl w-full h-[500px] object-cover"
              />
            </div>
            <div>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">Why Choose Residential Schooling?</h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Residential schooling isn't just about staying away from home—it's about creating an environment where your child can truly thrive.
              </p>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-blue-100 p-3 rounded-xl flex-shrink-0">
                    <Target className="w-6 h-6 text-blue-900" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Uninterrupted Focus</h3>
                    <p className="text-gray-600">A distraction-free environment that allows deep learning and concentrated academic pursuit.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-orange-100 p-3 rounded-xl flex-shrink-0">
                    <Shield className="w-6 h-6 text-orange-500" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Discipline & Structure</h3>
                    <p className="text-gray-600">Daily routines that build responsibility, time management, and self-discipline.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-green-100 p-3 rounded-xl flex-shrink-0">
                    <Sparkles className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Independence & Confidence</h3>
                    <p className="text-gray-600">Children learn to make decisions, solve problems, and become self-reliant individuals.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-purple-100 p-3 rounded-xl flex-shrink-0">
                    <Users className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Lifelong Friendships</h3>
                    <p className="text-gray-600">A close-knit community that creates bonds and memories that last a lifetime.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Campus Showcase */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 to-orange-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">A Campus Designed for Excellence</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">Green, sprawling facilities that inspire learning and growth</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
              <img 
                src="https://customer-assets.emergentagent.com/job_future-ready-school-2/artifacts/1sfqnft6_WhatsApp%20Image%202026-06-04%20at%203.39.53%20PM%20%281%29.jpeg"
                alt="Sports Facilities - Football"
                className="w-full h-64 object-cover"
              />
            </div>
            <div className="rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
              <img 
                src="https://customer-assets.emergentagent.com/job_future-ready-school-2/artifacts/6c83dx0v_WhatsApp%20Image%202026-06-04%20at%203.39.53%20PM.jpeg"
                alt="Holistic Development - Martial Arts"
                className="w-full h-64 object-cover"
              />
            </div>
            <div className="rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
              <img 
                src="https://customer-assets.emergentagent.com/job_future-ready-school-2/artifacts/r4a98omj_WhatsApp%20Image%202026-06-04%20at%203.39.52%20PM%20%282%29.jpeg"
                alt="Campus Sports Activities"
                className="w-full h-64 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Waitlist Section */}
      <section id="waitlist-section" className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-orange-500 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-600 rounded-full filter blur-3xl"></div>
        </div>
        <div className="max-w-4xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <div className="inline-block bg-orange-500 px-6 py-2 rounded-full text-sm font-semibold mb-6 animate-pulse">
              Limited Access • Exclusive Opportunity
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">Join the Founding Families Waitlist</h2>
            <p className="text-xl opacity-90 leading-relaxed max-w-3xl mx-auto mb-8">
              Be among the first to experience Sigma International School. Founding families receive exclusive benefits and priority access to admissions.
            </p>
            <div className="mb-10 flex justify-center">
              <WaitlistCounter variant="large" refreshKey={counterRefresh} />
            </div>
            <div className="grid md:grid-cols-3 gap-6 mb-12">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <Award className="w-10 h-10 mx-auto mb-3 text-orange-400" />
                <h3 className="font-bold mb-2">Priority Access</h3>
                <p className="text-sm opacity-80">First to know when admissions open</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <Sparkles className="w-10 h-10 mx-auto mb-3 text-orange-400" />
                <h3 className="font-bold mb-2">Exclusive Updates</h3>
                <p className="text-sm opacity-80">Campus progress & facility previews</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <Heart className="w-10 h-10 mx-auto mb-3 text-orange-400" />
                <h3 className="font-bold mb-2">Founding Member</h3>
                <p className="text-sm opacity-80">Special recognition & benefits</p>
              </div>
            </div>
          </div>
          <WaitlistForm onSuccess={handleNewSignup} />
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-br from-blue-50 to-orange-50 rounded-2xl p-12 shadow-lg">
            <BookOpen className="w-16 h-16 mx-auto mb-6 text-blue-900" />
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Backed by Bellwether International Schools</h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              Sigma International School is managed by Bellwether International Schools, a Mumbai-based educational group with a proven track record of excellence in education. We bring decades of expertise in creating transformative learning environments.
            </p>
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">Your Journey with Sigma</h2>
            <p className="text-xl text-gray-600">Key milestones on the path to excellence</p>
          </div>
          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gradient-to-b from-blue-900 via-orange-500 to-blue-900"></div>
            <div className="space-y-12">
              <div className="flex items-center">
                <div className="flex-1 text-right pr-8">
                  <div className="bg-white rounded-xl p-6 shadow-lg inline-block">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">June - August 2026</h3>
                    <p className="text-gray-600">Exclusive Waitlist Phase</p>
                  </div>
                </div>
                <div className="relative z-10 bg-blue-900 w-6 h-6 rounded-full border-4 border-white shadow-lg"></div>
                <div className="flex-1 pl-8"></div>
              </div>
              <div className="flex items-center">
                <div className="flex-1 pr-8"></div>
                <div className="relative z-10 bg-orange-500 w-6 h-6 rounded-full border-4 border-white shadow-lg"></div>
                <div className="flex-1 text-left pl-8">
                  <div className="bg-white rounded-xl p-6 shadow-lg inline-block">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">September 2026</h3>
                    <p className="text-gray-600">Admissions Open Officially</p>
                  </div>
                </div>
              </div>
              <div className="flex items-center">
                <div className="flex-1 text-right pr-8">
                  <div className="bg-white rounded-xl p-6 shadow-lg inline-block">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Academic Year 2027-28</h3>
                    <p className="text-gray-600">School Begins • Your Child's Journey Starts</p>
                  </div>
                </div>
                <div className="relative z-10 bg-green-600 w-6 h-6 rounded-full border-4 border-white shadow-lg"></div>
                <div className="flex-1 pl-8"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-900 to-blue-800 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl lg:text-5xl font-bold mb-6">Ready to Give Your Child the Best Future?</h2>
          <p className="text-xl opacity-90 mb-10 leading-relaxed">
            Join hundreds of forward-thinking parents who are securing their child's place in a transformative educational journey. Don't miss this exclusive opportunity.
          </p>
          <Button 
            onClick={scrollToWaitlist}
            size="lg"
            className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold px-12 py-7 text-xl shadow-2xl transition-all duration-300 hover:shadow-3xl hover:scale-110"
          >
            Join the Founding Families Now
            <ChevronRight className="ml-2 w-6 h-6" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-12 mb-8">
            <div>
              <img 
                src="/images/sigma-logo-1.png"
                alt="Sigma International School Logo"
                className="h-24 w-auto mb-4 bg-white p-2 rounded-lg inline-block"
              />
              <h3 className="text-2xl font-bold mb-2">Sigma International School</h3>
              <p className="text-gray-400 leading-relaxed">Premier Residential Learning • Launching AY 2027-28</p>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Contact Us</h4>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <Phone className="w-5 h-5 text-orange-400" />
                  <a href="tel:+919227991023" className="text-gray-300 hover:text-white transition-colors">+91 92279 91023</a>
                </div>
                <div className="flex items-center space-x-3">
                  <Mail className="w-5 h-5 text-orange-400" />
                  <a href="mailto:contact@alphalearningcentres.com" className="text-gray-300 hover:text-white transition-colors">contact@alphalearningcentres.com</a>
                </div>
                <div className="flex items-start space-x-3">
                  <MapPin className="w-5 h-5 text-orange-400 flex-shrink-0 mt-1" />
                  <p className="text-gray-300">Sigma International School<br/>Matar, Bharuch, Gujarat - 392035</p>
                </div>
              </div>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
              <div className="space-y-2">
                <p className="text-gray-400">www.sigmainternationalschool.com</p>
                <Button 
                  onClick={handleWhatsApp}
                  variant="outline"
                  className="mt-4 border-orange-500 text-orange-400 hover:bg-orange-500 hover:text-white transition-all duration-300"
                >
                  <MessageCircle className="mr-2 w-4 h-4" />
                  WhatsApp Us
                </Button>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p>Managed by Bellwether International Schools</p>
            <p className="mt-2">© 2026 Sigma International School. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;